// Fixed-timestep game loop. The simulation advances in constant slices so its
// behaviour is identical regardless of frame rate, while rendering (particles,
// floating numbers, screenshake) runs on real frame time for smoothness.
//
// Long real-time gaps (the tab was backgrounded for minutes) are clamped here
// and handled instead by the offline-progress system on the next load, so the
// loop never tries to simulate hours in one burst.

export type UpdateFn = (fixedDt: number) => void;
export type RenderFn = (frameDt: number) => void;

export class GameLoop {
  private accumulator = 0;
  private lastTime = 0;
  private rafId = 0;
  running = false;

  constructor(
    private readonly fixedDt: number,
    private readonly onUpdate: UpdateFn,
    private readonly onRender: RenderFn,
  ) {}

  start(): void {
    if (this.running) return;
    this.running = true;
    this.lastTime = performance.now();
    this.accumulator = 0;
    this.rafId = requestAnimationFrame(this.frame);
  }

  stop(): void {
    this.running = false;
    cancelAnimationFrame(this.rafId);
  }

  private frame = (now: number): void => {
    if (!this.running) return;

    let frameDt = (now - this.lastTime) / 1000;
    this.lastTime = now;
    // Clamp huge gaps (backgrounded tab). Offline progress covers long spans.
    if (frameDt > 0.25) frameDt = 0.25;

    this.accumulator += frameDt;
    let steps = 0;
    // Cap steps per frame to avoid a spiral of death if a frame ran very long.
    while (this.accumulator >= this.fixedDt && steps < 240) {
      this.onUpdate(this.fixedDt);
      this.accumulator -= this.fixedDt;
      steps++;
    }
    // Drop any residual backlog we could not catch up on this frame.
    if (steps >= 240) this.accumulator = 0;

    this.onRender(frameDt);
    this.rafId = requestAnimationFrame(this.frame);
  };
}
